/* eslint-disable no-extra-boolean-cast */
import qs from 'qs';
import { baseConfig } from '../../../config/algolia';
import { ContentfulMetafieldCollection } from '@jbhi-fi/apis/src/contentful/types';
const indexName = baseConfig.product_families_index ?? '';

type SearchStateToggle = {
  'banner_tags.label'?: boolean;
  'all_calculated_product_tags'?: boolean;
  onPromotion?: boolean;
  isMarketplace?: boolean; // excludesMarketplace products if true
};

export type SearchState = {
  [key: string]:
    | string
    | number
    | boolean
    | undefined
    | Array<string>
    | { [key: string]: string | number | boolean | undefined };
};

export type AlgoliaQueryState = {
  query?: string | undefined;
  page?: number | undefined;
  refinementList?: { [key: string]: string | number | boolean | undefined };
  range?: { [key: string]: string };
  hitsPerPage?: number | undefined;
  sortBy?: string | undefined;
  toggle?: SearchStateToggle | undefined;
};

/**
 * createRoutingURL
 * @description Creates a routing URL from the AlgoliaQueryState.
 * Converts the AlgoliaQueryState into a SearchState object and then converts it to a URL query string
 */
export const createRoutingURL = (algoliaQueryState: AlgoliaQueryState, isResultsPage = false): string => {
  const searchState: SearchState = {};

  // Query
  if (isResultsPage) {
    searchState.query = algoliaQueryState.query;
  }

  // Page
  if (!!algoliaQueryState.page) {
    searchState.page = algoliaQueryState.page;
  }

  // Hits Per Page
  if (!!algoliaQueryState.hitsPerPage) {
    searchState.hitsPerPage = algoliaQueryState.hitsPerPage;
  }

  // Sort By
  if (!!algoliaQueryState.sortBy && algoliaQueryState.sortBy !== indexName) {
    searchState.sortBy = algoliaQueryState.sortBy.replace(`${indexName}_`, '');
  }

  // Price Range
  if (!!algoliaQueryState.range && algoliaQueryState.range?.['facets.Price']) {
    searchState.Price = {} as { min?: string; max?: string };
    const splitPrice = algoliaQueryState.range['facets.Price'].split(':');

    if (splitPrice[0]) searchState.Price.min = splitPrice[0];
    if (splitPrice[1]) searchState.Price.max = splitPrice[1];
  }

  // Refinements
  if (algoliaQueryState.refinementList) {
    Object.keys(algoliaQueryState.refinementList).forEach(key => {
      if (!!algoliaQueryState?.refinementList?.[key]) {
        searchState[key.replace('facets.', '')] = algoliaQueryState.refinementList[key];
      }
    });
  }

  // Sale Items
  if (!!algoliaQueryState.toggle && algoliaQueryState.toggle.onPromotion) {
    searchState.saleItems = algoliaQueryState.toggle.onPromotion;
  }

  // Clearance Items
  if (!!algoliaQueryState.toggle && algoliaQueryState.toggle['banner_tags.label']) {
    searchState.clearanceItems = 'true';
  }

  // Redhotdeal Items
  if (!!algoliaQueryState.toggle && algoliaQueryState.toggle['all_calculated_product_tags']) {
    searchState.redhotdealItems = 'true';
  }

  // Marketplace Items
  // Note: `isMarketplace=true` will appear as `excludeMarketplace=true` in the URL
  if (!!algoliaQueryState.toggle && algoliaQueryState.toggle.isMarketplace) {
    searchState.excludeMarketplace = 'true';
  }

  return Object.keys(searchState).length ? `?${qs.stringify(searchState, { indices: false })}` : ``;
};

/**
 * queryStateToUrl
 * @description Creates a URL from an Algolia search state object using createRoutingURL
 */
export const queryStateToUrl = (
  location: typeof window.location,
  algoliaQueryState: AlgoliaQueryState,
  isResultsPage = false
) => (algoliaQueryState ? `${location.pathname}${createRoutingURL(algoliaQueryState, isResultsPage)}` : '');

/**
 * urlToSearchState
 * @description Converts the URL into the custom search state
 */
export const urlToSearchState = (
  { search }: typeof window.location,
  collection?: ContentfulMetafieldCollection | null,
  isResultsPage?: boolean
): SearchState => {
  const urlQs = qs.parse(search.slice(1)) as SearchState;
  const searchState: SearchState = {};
  const queryKeys = [
    'page',
    'hitsPerPage',
    'sortBy',
    'Price',
    'saleItems',
    'banner_tags.label',
    'all_calculated_product_tags',
    'excludeMarketplace',
  ];

  // Query
  if (isResultsPage) {
    queryKeys.push('query');
    searchState.query = urlQs.query;
  }

  // Page
  searchState.page = urlQs.page || 1;

  // Refinements
  searchState.refinementList = Object.keys(urlQs)
    .filter(key => !queryKeys.includes(key))
    .reduce((obj: { [key: string]: any }, key: string) => {
      obj[`facets.${key}`] = Array.isArray(urlQs[key]) ? urlQs[key] : [urlQs[key]];
      return obj;
    }, {});

  // Range
  searchState.range = Object.keys(urlQs)
    .filter(key => ['Price'].includes(key))
    .reduce((obj: { [key: string]: any }, key) => {
      const rangeObj = urlQs[key] as { min?: string; max?: string };
      const min = rangeObj?.min || '';
      const max = rangeObj?.max || '';

      obj[`facets.${key}`] = `${min}:${max}`;
      return obj;
    }, {});

  // Hits Per Page
  searchState.hitsPerPage = urlQs.hitsPerPage || 36;

  // Sort By
  searchState.sortBy = !!urlQs.sortBy
    ? `${indexName}_${urlQs.sortBy}`
    : collection?.sortMethod && !isResultsPage
    ? indexName + collection.sortMethod
    : indexName;

  // Toggle
  const toggle: SearchStateToggle = {};
  if (urlQs.saleItems) {
    toggle.onPromotion = urlQs.saleItems === 'true';
  }
  if (urlQs.clearanceItems) {
    toggle['banner_tags.label'] = true;
  }
  if (urlQs.redhotdealItems) {
    toggle['all_calculated_product_tags'] = true;
  }
  if (urlQs.excludeMarketplace) {
    toggle.isMarketplace = urlQs.excludeMarketplace === 'true';
  }
  if (Object.keys(toggle).length) searchState.toggle = toggle;

  return searchState;
};
